import { useCallback, useState } from 'react';
import { Box, Button, Column, Row, Tooltip, Typography } from '@tempmee/react';
import { OutlineMailIcon, SolidInformationCircleIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { EmbeddedInviteList } from '~/components/ShiftRequestRedesign/AddInvites/EmbeddedInviteList';
import { useInviteFavoriteToJobMutation } from '~/domain/Jobs';
import { type IJob } from '~/models';
import { fireInviteSent, fireShiftDetailsInviteClicked } from '~/posthogs/events';
import { InviteCountCaption } from '../InviteCountCaption';

interface IInviteSectionProps {
  job: IJob;
}

const INVITE_TOOLTIP =
  'Select professionals to invite to this shift. Invited professionals receive an alert for the days they’re available.';

/**
 * Left column of the redesigned open Shift Details page: the inline invite list
 * (reused from the Shift Request flow), an Invite button that sends the selected
 * invites, and a confirmation popup on success. Already-invited professionals
 * render as non-selectable "Sent" rows.
 */
export const InviteSection = ({ job }: IInviteSectionProps) => {
  const { mutate: inviteFavoriteToJob, isPending } = useInviteFavoriteToJobMutation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  // Remounting the list after a successful send resets its internal selection
  // and lets it re-render the freshly invited pros as "Sent".
  const [listKey, setListKey] = useState(0);

  const officeId = job.offer_owner?.id;

  const handleInvite = useCallback(() => {
    if (selectedIds.length === 0) return;

    fireShiftDetailsInviteClicked({ shiftId: job.id });

    inviteFavoriteToJob(
      { jobId: job.id, userIds: selectedIds, officeId },
      {
        onSuccess: () => {
          fireInviteSent({
            source: 'Shift Details',
            shiftId: job.id,
            totalInvitesSent: selectedIds.length,
          });
          // The mutation invalidates the job's React Query cache, so the screen's
          // useGetJob refetches and the "Sent" state + invite count update on their
          // own — no manual Redux refresh needed.
          setShowSuccess(true);
          setSelectedIds([]);
          setListKey((key) => key + 1);
        },
      },
    );
  }, [inviteFavoriteToJob, job.id, officeId, selectedIds]);

  return (
    <Column gap={6} data-cy="shift-details-invite-section" width="100%">
      <Row align="center" gap={2}>
        <Typography variant="subtitle1" color={colors.neutral[600]} bold>
          Invite Professionals:
        </Typography>
        <Tooltip content={INVITE_TOOLTIP}>
          <SolidInformationCircleIcon size={20} color={colors.neutral[300]} />
        </Tooltip>
      </Row>

      <EmbeddedInviteList
        key={listKey}
        jobId={job.id}
        officeId={officeId}
        professionType={job.profession}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      <Column align="center" gap={2}>
        <Button
          startIcon={<OutlineMailIcon size={20} />}
          data-cy="shift-details-invite-button"
          size="small"
          disabled={selectedIds.length === 0}
          loading={isPending}
          onClick={handleInvite}
        >
          Invite
        </Button>
        <InviteCountCaption count={job.favoriteInvites?.length ?? 0} />
      </Column>

      {showSuccess && (
        <ConfirmPopup
          dataCy="shift-details-invite-success"
          rightButtonDataCy="shift-details-invite-success-close"
          Icon={() => (
            <Box pb={5}>
              <AnimatedCheckCircle />
            </Box>
          )}
          title="Invite(s) Successfully Sent!"
          description="This shift request has been successfully sent to your selected professionals."
          rightButtonText="Close"
          rightButtonAction={() => setShowSuccess(false)}
        />
      )}
    </Column>
  );
};
